import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';
import { ResizeService } from '../../services/resize/resize.service';
import { ResizeableComponent } from '../design/resizeable/resizeable.component';
import {
  Language,
  LanguageService,
} from '../../services/language/language.service';
import { SelectOption } from '../../interfaces/select-option.interface';
import {
  BehaviorSubject,
  Observable,
  of,
  skip,
  Subscription,
} from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  switchMap,
} from 'rxjs/operators';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import {
  TAXI_CALCULATOR,
  TARIFF_PAGE,
  RAV_KAV_DETAILS,
  PUBLIC_TRANSPORT_UPDATES,
  SUBMIT_COMPLAIN,
  BUS,
  TRAIN,
  PUBLIC_TAXI,
  CONTACT_US,
  SEARCH,
  NO_RESULT_FOUND,
  LOGO_ALT,
  OPEN_SIDE_MENU,
  CHOOSE_LANGUAGE,
  LANGUAGE_MENU_BUTTON_ARIA_LABEL,
  LANGUAGE_SELECTED,
  OPENS_IN_NEW_TAB,
  SUBMENU_FOR,
} from '../../../modules/route-planner/constants/multi-language.constants';
import { ApiMenuItem, MenuItem } from '../route-planner/side-menu/side-menu.component';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import {
  WebsiteCmsSearchService,
  WebsiteCmsSearchResultItem,
} from 'src/app/@shared/services/website-cms-search/website-cms-search.service';
import { ContactInfo } from '../contact-view/contact.view.component';
import {
  MINISTRY_CONTACT_DEFAULT_INFO,
  MinistryContactInfoService,
} from '../../services/ministry-contact-info/ministry-contact-info.service';

export interface NavigationButton {
  label: string;
  link?: string;
  translationKey?: any;
}


@Component({
  selector: 'app-nav-bar',
  templateUrl: './nav-bar.component.html',
  styleUrls: ['./nav-bar.component.scss'],
  standalone: false
})
export class NavBarComponent implements OnInit, OnDestroy {
  options$: Observable<SelectOption<string>[]>;
  tariffsOptions$: Observable<SelectOption<string>[]>;
  direction: 'rtl' | 'ltr' = 'rtl';
  menuItems: MenuItem[] = [];

  // Dropdown state management
  openDropdownIndex: number | null = null;
  activeMenuTrigger!: CdkOverlayOrigin;
  menuDropdownPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
  ];

  // Language dropdown state
  langOpen = false;
  currentLang: string = 'hebrew';
  languages = [
    { code: 'hebrew', label: 'עברית' },
    { code: 'english', label: 'English' },
    { code: 'arabic', label: 'عربي' }
  ];
  langOverlayPositions: ConnectedPosition[] = [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
  ];

  // Sidebar state
  isSidebarOpen = false;

  /** Matches SCSS breakpoint $bp-xxl (1440px): desktop strip nav vs burger. */
  isDesktopNavVisible =
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(min-width: 1440px)').matches
      : false;
  private desktopNavMql?: MediaQueryList;
  private readonly onDesktopNavMqlChange = (): void => this.syncDesktopNav();

  languageFormControl = new FormControl<string>(
    this.languageService.getSelectedLanguage().language
  );
  submitRequestText = SUBMIT_COMPLAIN.hebrew;
  contactUsText = CONTACT_US.hebrew;
  searchAriaLabel = SEARCH.hebrew;
  searchNoResultsText = NO_RESULT_FOUND.hebrew;
  logoAltText = LOGO_ALT.hebrew;
  openSideMenuAriaLabel = OPEN_SIDE_MENU.hebrew;
  /**
   * Translated bits used to build accessible names for the language switcher
   * and the new-tab leaf links. See multi-language.constants.ts for templates.
   */
  chooseLanguageText = CHOOSE_LANGUAGE.hebrew;
  private languageMenuButtonTemplate = LANGUAGE_MENU_BUTTON_ARIA_LABEL.hebrew;
  private languageSelectedSuffix = LANGUAGE_SELECTED.hebrew;
  private opensInNewTabSuffix = OPENS_IN_NEW_TAB.hebrew;
  private submenuForTemplate = SUBMENU_FOR.hebrew;
  isSearchOpen = false;
  searchQuery = '';
  private readonly searchQuerySubject = new BehaviorSubject<string>('');
  searchResults$!: Observable<WebsiteCmsSearchResultItem[]>;
  searchInputStyle: { top: string; left: string; width: string } | null = null;
  private languageSubscription: Subscription;
  private ministryContactInfoSubscription: Subscription;

  /** Same modal + CMS static-variable flow as mot-website-client header. */
  isContactOpen = false;
  contactInfo: ContactInfo = { ...MINISTRY_CONTACT_DEFAULT_INFO };

  constructor(
    private readonly languageService: LanguageService,
    private readonly http: HttpClient,
    private readonly websiteCmsSearch: WebsiteCmsSearchService,
    private readonly ministryContactInfo: MinistryContactInfoService
  ) { }

  ngOnInit(): void {
    if (typeof window !== 'undefined' && window.matchMedia) {
      this.desktopNavMql = window.matchMedia('(min-width: 1440px)');
      this.syncDesktopNav();
      this.desktopNavMql.addEventListener('change', this.onDesktopNavMqlChange);
    }

    this.getLanguageOptions();
    this.initializeMultiLanguageTexts();
    this.fetchMenuItems(this.languageService.getSelectedLanguage().code);

    this.searchResults$ = this.searchQuerySubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((query) => {
        if (!query || query.trim().length < 2) {
          return of([]);
        }
        const languageId = this.languageService.getSelectedLanguage().code;
        return this.websiteCmsSearch.search(languageId, query.trim());
      })
    );

    // Set current language
    this.currentLang = this.languageService.getSelectedLanguage().language;

    // Only handle language changes from the form control
    this.languageFormControl.valueChanges
      .pipe(skip(1))
      .subscribe((language: string) => {
        this.languageService.changeLanguage(language);
      });

    this.ministryContactInfoSubscription =
      this.ministryContactInfo.contactInfo$.subscribe((info) => {
        this.contactInfo = info;
      });
  }

  ngOnDestroy(): void {
    this.desktopNavMql?.removeEventListener('change', this.onDesktopNavMqlChange);
    if (this.languageSubscription) {
      this.languageSubscription.unsubscribe();
    }
    this.ministryContactInfoSubscription?.unsubscribe();
    document.body.classList.remove('modal-open');
  }

  private syncDesktopNav(): void {
    this.isDesktopNavVisible = this.desktopNavMql?.matches ?? false;
  }

  navigations: NavigationButton[] = [
    {
      label: TAXI_CALCULATOR.hebrew,
      link: '/taxi-calculator',
      translationKey: TAXI_CALCULATOR,
    },
    {
      label: TARIFF_PAGE.hebrew,
      link: '/tariff-page',
      translationKey: TARIFF_PAGE,
    },
    {
      label: RAV_KAV_DETAILS.hebrew,
      link: '/rav-kav-details',
      translationKey: RAV_KAV_DETAILS,
    },
    {
      label: PUBLIC_TRANSPORT_UPDATES.hebrew,
      link: '/transport-updates',
      translationKey: PUBLIC_TRANSPORT_UPDATES,
    },
  ];

  getLanguageOptions() {
    const languageOptions = this.languageService.languageOptions;
    const selectOptions: SelectOption<string>[] = Object.values(
      languageOptions
    ).map((lang: Language) => ({
      label: lang.label,
      value: lang.language,
      selected:
        this.languageService.getSelectedLanguage().language === lang.language,
    }));

    this.options$ = of(selectOptions);
  }

  initializeMultiLanguageTexts() {
    this.languageSubscription =
      this.languageService.selectedLanguage$.subscribe((language) => {
        this.direction = language.direction;
        this.currentLang = language.language;
        this.updateNavigationLabels(language);
        this.updateStaticTexts(language);
        this.updateTariffsOptions(language);

        // Fetch new menu items when language changes
        this.fetchMenuItems(language.code);

        // Update form control value without triggering valueChanges
        if (this.languageFormControl.value !== language.language) {
          this.languageFormControl.setValue(language.language, {
            emitEvent: false,
          });
        }
      });
  }

  updateNavigationLabels(language: Language) {
    this.navigations.forEach((nav) => {
      if (nav.translationKey) {
        nav.label = nav.translationKey[language.language];
      }
    });
  }

  updateTariffsOptions(language: Language) {
    const tariffsMainLabel = TARIFF_PAGE[language.language];
    const busTariffsLabel = `${tariffsMainLabel} - ${BUS[language.language]}`;
    const trainTariffsLabel = `${tariffsMainLabel} - ${TRAIN[language.language]
      }`;
    const taxiTariffsLabel = `${tariffsMainLabel} - ${PUBLIC_TAXI[language.language]
      }`;

    const tariffsOptions: SelectOption<string>[] = [
      { label: tariffsMainLabel, value: 'tariff-page', selected: true },
      { label: busTariffsLabel, value: 'bus-tariffs' },
      { label: trainTariffsLabel, value: 'train-tariffs' },
      { label: taxiTariffsLabel, value: 'taxi-tariffs' },
    ];

    this.tariffsOptions$ = of(tariffsOptions);
  }

  updateStaticTexts(language: Language) {
    this.submitRequestText = SUBMIT_COMPLAIN[language.language];
    this.contactUsText = CONTACT_US[language.language];
    this.searchAriaLabel = SEARCH[language.language];
    this.searchNoResultsText = NO_RESULT_FOUND[language.language];
    this.logoAltText = LOGO_ALT[language.language];
    this.openSideMenuAriaLabel = OPEN_SIDE_MENU[language.language];
    this.chooseLanguageText = CHOOSE_LANGUAGE[language.language];
    this.languageMenuButtonTemplate = LANGUAGE_MENU_BUTTON_ARIA_LABEL[language.language];
    this.languageSelectedSuffix = LANGUAGE_SELECTED[language.language];
    this.opensInNewTabSuffix = OPENS_IN_NEW_TAB[language.language];
    this.submenuForTemplate = SUBMENU_FOR[language.language];
  }

  private fetchMenuItems(languageId: number): void {
    const apiUrl = environment.bo_be_service_url + 'menu/language/' + languageId + '/detailed';

    this.http.get<ApiMenuItem[]>(apiUrl).subscribe({
      next: (response) => {
        this.menuItems = this.mapApiResponseToMenuItems(response);
      },
      error: (error) => {
        console.error('Error fetching menu items:', error);
        // Set empty array as fallback
        this.menuItems = [];
      }
    });
  }

  /**
   * Map API response to MenuItem array
   */
  private mapApiResponseToMenuItems(apiItems: ApiMenuItem[]): MenuItem[] {
    return apiItems
      .filter(item => item.isVisible && item.parentId === null) // Only top-level visible items
      .sort((a, b) => a.orderIndex - b.orderIndex) // Sort by orderIndex
      .map(item => this.mapApiItemToMenuItem(item));
  }

  /**
   * Map single API item to MenuItem
   */
  private mapApiItemToMenuItem(apiItem: ApiMenuItem): MenuItem {
    const menuItem: MenuItem = {
      title: apiItem.title,
      url: apiItem.url,
      children: []
    };

    // Map children if they exist
    if (apiItem.children && apiItem.children.length > 0) {
      menuItem.children = apiItem.children
        .filter(child => child.isVisible)
        .sort((a, b) => a.orderIndex - b.orderIndex)
        .map(child => this.mapApiItemToMenuItem(child));
    }

    return menuItem;
  }

  handleMenuClick(url: string): void {
    window.open(environment.website_url + url, '_blank');
  }

  /**
   * Absolute href for a CMS menu item (top-level or nested).
   * Used so leaf items can be real <a [href]> with target="_blank" — keyboard
   * focusable, announced as "link" by SR, and supporting middle/right-click.
   */
  menuItemHref(url: string | undefined | null): string {
    if (!url) {
      return environment.website_url;
    }
    return environment.website_url + url;
  }

  /**
   * Accessible name for a leaf menu link (no children).
   * Announces the item title plus "(opens in a new tab)" so SR users know
   * activation will spawn a new window. Mirrors mot-website-client behaviour.
   */
  menuLeafAriaLabel(title: string | undefined | null): string {
    const t = (title ?? '').trim();
    if (!t) return this.opensInNewTabSuffix;
    return `${t}, ${this.opensInNewTabSuffix}`;
  }

  /**
   * Accessible name for a submenu's container (role="group").
   * Interpolates the parent item's title so SR users know which top-level
   * trigger the submenu belongs to (matches mot-website-client header).
   */
  submenuAriaLabel(title: string | undefined | null): string {
    const t = (title ?? '').trim();
    return this.submenuForTemplate.replace('{0}', t || '');
  }

  /**
   * Accessible name for the language switcher trigger.
   * "Choose site language. Current: {x}" — gives full context before
   * user activates the disclosure (matches mot-website-client header).
   */
  get languageMenuButtonAriaLabel(): string {
    return this.languageMenuButtonTemplate.replace(
      '{0}',
      this.currentLangLabel
    );
  }

  /**
   * Accessible name for a single language option.
   * Adds a "selected" suffix when the option matches the active locale so the
   * active item is announced unambiguously (toggle-button group pattern).
   */
  langOptionAriaLabel(l: { code: string; label: string }): string {
    if (l.code !== this.currentLang) {
      return l.label;
    }
    return `${l.label}, ${this.languageSelectedSuffix}`;
  }

  /**
   * Return focus to the disclosure trigger for the given submenu index.
   * Used after closing a submenu via Escape (APG menu-button pattern).
   */
  private focusMainNavTrigger(index: number): void {
    if (typeof document === 'undefined') return;
    setTimeout(() => {
      const trigger = document.getElementById(`main-nav-trigger-${index}`);
      trigger?.focus({ preventScroll: true });
    }, 0);
  }

  handleSubmitRequestClick(): void {
    const url = this.languageService.getSelectedLanguage().symbol === 'he' ? environment.complaintUrl : environment.complaintUrlMultiLanguage;
    window.open(url, '_blank');
  }

  /**
   * Toggle dropdown for navigation item
   */
  toggleDropdown(index: number, event: Event, trigger: CdkOverlayOrigin): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.openDropdownIndex === index) {
      this.openDropdownIndex = null;
    } else {
      this.activeMenuTrigger = trigger;
      this.openDropdownIndex = index;
    }
  }

  /**
   * Check if dropdown is open
   */
  isDropdownOpen(index: number): boolean {
    return this.openDropdownIndex === index;
  }

  /**
   * Close dropdown when clicking outside
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (this.isSearchOpen && !target.closest('.search-container')) {
      this.closeSearch();
    }
    const clickedInside = target.closest('.nav-item');
    const clickedInsideLang = target.closest('.lang');

    if (!clickedInside && this.openDropdownIndex !== null) {
      this.openDropdownIndex = null;
    }

    if (!clickedInsideLang && this.langOpen) {
      this.langOpen = false;
    }
  }

  @HostListener('document:keydown.escape')
  onDocumentEscape(): void {
    if (this.isContactOpen) {
      this.closeContact();
      return;
    }
    if (this.isSearchOpen) {
      this.closeSearch();
      return;
    }
    if (this.langOpen) {
      this.langOpen = false;
      const langBtn = document.querySelector<HTMLButtonElement>(
        '#main-nav .lang .lang-btn'
      );
      langBtn?.focus({ preventScroll: true });
      return;
    }
    if (this.openDropdownIndex !== null) {
      const closingIndex = this.openDropdownIndex;
      this.openDropdownIndex = null;
      this.focusMainNavTrigger(closingIndex);
    }
  }

  /**
   * Panel position: top aligned to header (10px); horizontal geometry like website (width expand).
   */
  openSearch(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const searchButton = document.querySelector(
      '#main-nav button.icon.search'
    ) as HTMLElement | null;

    const minLeftMargin = 16;
    const maxWidth = 500;
    const isRtl = this.direction === 'rtl';

    if (searchButton && typeof window !== 'undefined') {
      const rect = searchButton.getBoundingClientRect();
      if (isRtl) {
        const availableWidth = rect.right - minLeftMargin;
        const finalWidth = Math.min(maxWidth, availableWidth);
        const finalLeft = Math.max(minLeftMargin, rect.right - finalWidth);
        this.searchInputStyle = {
          top: '10px',
          left: `${rect.right - 40}px`,
          width: '40px',
        };
        setTimeout(() => {
          if (this.isSearchOpen) {
            this.searchInputStyle = {
              top: '10px',
              left: `${finalLeft}px`,
              width: `${finalWidth}px`,
            };
          }
        }, 10);
      } else {
        const availableWidth = window.innerWidth - rect.left - minLeftMargin;
        const finalWidth = Math.min(maxWidth, availableWidth);
        this.searchInputStyle = {
          top: '10px',
          left: `${rect.left}px`,
          width: '40px',
        };
        setTimeout(() => {
          if (this.isSearchOpen) {
            this.searchInputStyle = {
              top: '10px',
              left: `${rect.left}px`,
              width: `${finalWidth}px`,
            };
          }
        }, 10);
      }
    } else if (typeof window !== 'undefined') {
      this.searchInputStyle = {
        top: '10px',
        left: `${minLeftMargin}px`,
        width: `${Math.min(maxWidth, window.innerWidth - minLeftMargin * 2)}px`,
      };
    }

    this.isSearchOpen = true;
    this.syncBodyModalOpen();

    setTimeout(() => {
      const searchInput = document.querySelector(
        '#header-search-panel .search-input'
      ) as HTMLInputElement | null;
      searchInput?.focus();
    }, 350);
  }

  closeSearch(): void {
    this.isSearchOpen = false;
    this.searchQuery = '';
    this.searchQuerySubject.next('');
    this.searchInputStyle = null;
    this.syncBodyModalOpen();
  }

  onSearchQueryInput(event: Event): void {
    this.searchQuery = (event.target as HTMLInputElement).value;
    this.searchQuerySubject.next(this.searchQuery);
  }

  navigateToSearchResult(result: WebsiteCmsSearchResultItem): void {
    const symbol = this.languageService.getSelectedLanguage().symbol;
    this.closeSearch();
    this.websiteCmsSearch.openPageResultInNewWindow(result, symbol);
  }

  onSearchEnter(event: Event): void {
    const ke = event as KeyboardEvent;
    if (ke.key !== 'Enter' || this.searchQuery.trim().length < 2) {
      return;
    }
    const q = this.searchQuery.trim();
    const symbol = this.languageService.getSelectedLanguage().symbol;
    this.closeSearch();
    this.websiteCmsSearch.openFullSearchInNewWindow(q, symbol);
  }

  /**
   * Get current language label
   */
  get currentLangLabel(): string {
    return this.languages.find(l => l.code === this.currentLang)?.label ?? 'עברית';
  }

  /**
   * Toggle language dropdown
   */
  toggleLang(): void {
    this.langOpen = !this.langOpen;
  }

  /**
   * Select language
   */
  selectLang(code: string): void {
    this.langOpen = false;
    this.currentLang = code;

    // languageService.changeLanguage will trigger subscription in initializeMultiLanguageTexts
    // which will call fetchMenuItems with the new language code
    this.languageService.changeLanguage(code);
  }

  /**
   * Toggle sidebar menu
   */
  toggleSidebar(): void {
    this.isSidebarOpen = !this.isSidebarOpen;
    this.syncBodyModalOpen();
  }

  /**
   * Close sidebar menu
   */
  closeSidebar(): void {
    this.isSidebarOpen = false;
    this.syncBodyModalOpen();
  }

  openContact(): void {
    this.isContactOpen = true;
    this.syncBodyModalOpen();
  }

  closeContact(): void {
    this.isContactOpen = false;
    this.syncBodyModalOpen();
  }

  private syncBodyModalOpen(): void {
    if (typeof document === 'undefined') {
      return;
    }
    if (this.isSidebarOpen || this.isContactOpen || this.isSearchOpen) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
  }

}

